import { Item, Shape } from "dsabp-js"
import { CanvasRenderingContext2D, Image, createCanvas } from "../canvas.js"
import { rendererResolvers } from "../main.js"
import { Hull, TileFace } from "../types.js"
import { getImage, toImage } from "../util.js"

const TILE_SIZE = 80
const BLOCKS_START_INDEX = 8 * 6 - 1 // space above block tiles
const BORDERS_START_INDEX = 8 * 3 - 1
const borderColors = new Map<Item, string>()
	.set(Item.BLOCK, "#2c2c2c")
	.set(Item.BLOCK_ICE_GLASS, "#3a535e")
	.set(Item.BLOCK_ITEM_NET, "#6e5639")
	.set(Item.BLOCK_WALKWAY, "#000000")
	.set(Item.BLOCK_HYPER_RUBBER, "#000000")

export const connectedTileTypes = new Set([Item.BLOCK, Item.BLOCK_ICE_GLASS])

const CACHE_KEY_ATLAS = "Tile_atlasSubWorld"
let atlasWidth: number

const i = rendererResolvers.push(
	({ main, item, shape, other }) => {
		if (!other?.hull && !item.isBlock)
			return

		const connectedFaces = other?.connectedTileFaces

		return {
			discriminator: (other?.hull ?? item.id)
				+ "_" + (shape ?? Shape.BLOCK).enumValue
				+ "_" + (connectedFaces ?? "-"),
			async render() {
				const canvas = createCanvas(TILE_SIZE, TILE_SIZE)
				const ctx = canvas.getContext("2d")
				const isShaped = item.buildInfo?.[0].block_shaped

				// draw shape
				if (isShaped && shape != null && shape != Shape.BLOCK) {
					ctx.beginPath()
					for (let i = 0; i < shape.vertices.length; i++)
						ctx[i == 0 ? "moveTo" : "lineTo"]((shape.vertices[i].x + 0.5) * canvas.width, (-shape.vertices[i].y + 0.5) * canvas.height)
					ctx.closePath()
					ctx.clip()
					ctx.clearRect(0, 0, canvas.width, canvas.height)
					ctx.clip("nonzero")
				}

				// draw tile
				await drawTile(main.imageCache, ctx, other?.hull ?? item)
				if (isShaped)
					await drawBorder(main.imageCache, ctx, item, shape ?? Shape.BLOCK, connectedFaces)
				ctx.globalCompositeOperation = "source-over"

				return [{ image: await toImage(canvas) }]
			}
		}
	}
)

export const tileRendererResolver = rendererResolvers[i - 1]

const borderMap: Map<Shape, [border: number, angle?: number, flip?: "H" | "V"]> = new Map()
	.set(Shape.RAMP_DL, [5, 180])
	.set(Shape.RAMP_DR, [5, 90])
	.set(Shape.RAMP_UL, [5, -90])
	.set(Shape.RAMP_UR, [5])
	.set(Shape.SLAB_D, [6, 180])
	.set(Shape.SLAB_L, [6, -90])
	.set(Shape.SLAB_R, [6, 90])
	.set(Shape.SLAB_U, [6])
	.set(Shape.HALF_RAMP_2_D, [8, 180])
	.set(Shape.HALF_RAMP_2_DI, [8, 180, "H"])
	.set(Shape.HALF_RAMP_2_L, [8, -90])
	.set(Shape.HALF_RAMP_2_LI, [8, -90, "V"])
	.set(Shape.HALF_RAMP_2_R, [8, 90])
	.set(Shape.HALF_RAMP_2_RI, [8, 90, "V"])
	.set(Shape.HALF_RAMP_2_U, [8])
	.set(Shape.HALF_RAMP_2_UI, [8, 0, "H"])
	.set(Shape.HALF_RAMP_1_D, [9, 180])
	.set(Shape.HALF_RAMP_1_DI, [9, 180, "H"])
	.set(Shape.HALF_RAMP_1_L, [9, -90])
	.set(Shape.HALF_RAMP_1_LI, [9, -90, "V"])
	.set(Shape.HALF_RAMP_1_R, [9, 90])
	.set(Shape.HALF_RAMP_1_RI, [9, 90, "V"])
	.set(Shape.HALF_RAMP_1_U, [9])
	.set(Shape.HALF_RAMP_1_UI, [9, 0, "H"])
	.set(Shape.HALF_RAMP_3_D, [10, 180])
	.set(Shape.HALF_RAMP_3_DI, [10, 180, "H"])
	.set(Shape.HALF_RAMP_3_L, [10, -90])
	.set(Shape.HALF_RAMP_3_LI, [10, -90, "V"])
	.set(Shape.HALF_RAMP_3_R, [10, 90])
	.set(Shape.HALF_RAMP_3_RI, [10, 90, "V"])
	.set(Shape.HALF_RAMP_3_U, [10])
	.set(Shape.HALF_RAMP_3_UI, [10, 0, "H"])
	.set(Shape.QUARTER_DL, [11, 180])
	.set(Shape.QUARTER_DR, [11, 90])
	.set(Shape.QUARTER_UL, [11, -90])
	.set(Shape.QUARTER_UR, [11])
	.set(Shape.QUARTER_RAMP_DL, [12, 180])
	.set(Shape.QUARTER_RAMP_DR, [12, 90])
	.set(Shape.QUARTER_RAMP_UL, [12, -90])
	.set(Shape.QUARTER_RAMP_UR, [12])
	.set(Shape.BEVEL_DL, [13, 180])
	.set(Shape.BEVEL_DR, [13, 90])
	.set(Shape.BEVEL_UL, [13, -90])
	.set(Shape.BEVEL_UR, [13])

async function drawBorder(imageCache: Record<string, Image>, ctx: CanvasRenderingContext2D, item: Item, shape: Shape, faceBits?: number) {
	const color = borderColors.get(item)

	// draw the shape related border
	const args = borderMap.get(shape)
	if (args)
		await drawTile(imageCache, ctx, args[0] + BORDERS_START_INDEX, args[1], args[2], color)

	// draw the main border
	f: if (connectedTileTypes.has(item)) {
		// whether the tile is connected to the input face
		const c = (f: TileFace) => faceBits & f

		// amount of connected sides of the tile
		let amount = 0
		if (c(TileFace.Bottom)) ++amount
		if (c(TileFace.Left)) ++amount
		if (c(TileFace.Right)) ++amount
		if (c(TileFace.Top)) ++amount

		if (amount == 4) break f

		let mainIndex: number, angle: number

		if (!amount) {
			mainIndex = 4
		} else if (amount == 1) {
			mainIndex = 3
			angle = c(TileFace.Bottom) ? null :
				c(TileFace.Left) ? 90 :
					c(TileFace.Top) ? 180 :
						-90
		} else if (amount == 2) {
			if (c(TileFace.Top) && c(TileFace.Bottom)) {
				mainIndex = 2
				angle = 90
			} else if (c(TileFace.Left) && c(TileFace.Right)) {
				mainIndex = 2
			} else {
				mainIndex = 1
				angle = c(TileFace.Bottom) && c(TileFace.Left) ? null :
					c(TileFace.Left) && c(TileFace.Top) ? 90 :
						c(TileFace.Top) && c(TileFace.Right) ? 180 :
							-90
			}
		} else if (amount == 3) {
			mainIndex = 0
			if (!(c(TileFace.Right)))
				angle = 90
			else if (!(c(TileFace.Bottom)))
				angle = 180
			else if (!(c(TileFace.Left)))
				angle = -90
		}

		await drawTile(imageCache, ctx, mainIndex + BORDERS_START_INDEX, angle, null, color)
	}
}

/** Draw a certain tile from the atlas */
async function drawTile(imageCache: Record<string, Image>, ctx: CanvasRenderingContext2D, thing: Item | Hull | number, angle?: number, flip?: "H" | "V", color?: string) {
	let atlasImage = imageCache[CACHE_KEY_ATLAS]
	if (!atlasImage) {
		atlasImage = imageCache[CACHE_KEY_ATLAS] = await getImage("tiles_subworld")
		atlasWidth = atlasImage.width / TILE_SIZE
	}

	let index: number
	if (thing instanceof Item) {
		index = thing.buildInfo[0].block + BLOCKS_START_INDEX
	} else if (typeof thing == "string") {
		if (thing == "HULL_CORNER")
			index = 1
		else if (thing == "HULL_H")
			index = 2
		else if (thing == "HULL_V")
			index = 3
		index += BLOCKS_START_INDEX
	} else {
		index = thing
	}

	const col = (index + 1) % atlasWidth
	const row = Math.floor((index + 1) / atlasWidth)

	const canvas2 = createCanvas(TILE_SIZE, TILE_SIZE)
	const ctx2 = canvas2.getContext("2d")

	if (angle != null || flip) {
		const half = TILE_SIZE / 2
		ctx2.save()
		ctx2.translate(half, half)
		if (flip == "H")
			ctx2.scale(-1, 1)
		else if (flip == "V")
			ctx2.scale(1, -1)
		ctx2.rotate(angle * Math.PI / 180)
		ctx2.translate(-half, -half)
	}

	if (color) {
		ctx2.fillStyle = color
		ctx2.globalCompositeOperation = "multiply"
		ctx2.fillRect(0, 0, TILE_SIZE, TILE_SIZE)
		ctx2.globalCompositeOperation = "destination-in"
	}

	ctx2.drawImage(
		atlasImage,
		col * TILE_SIZE,
		row * TILE_SIZE,
		TILE_SIZE, TILE_SIZE,
		0, 0,
		TILE_SIZE, TILE_SIZE
	)
	if (angle != null || flip)
		ctx2.restore()

	if (color) ctx2.globalCompositeOperation = "source-over"
	ctx.drawImage(canvas2, 0, 0)
}
